import { Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import moment, { Moment } from 'moment';
import { Subscription } from 'rxjs';

import { DD_MM_YYYY_DATE_FORMATS, getMatDateLocale } from '../../helpers/date-formats.helper';

const ISO_DATE = 'YYYY-MM-DD';

interface ChangeHandler {
	(value: string): void;
}

interface TouchHandler {
	(): void;
}

const NOOP = (): void => undefined;

/**
 * A date field that shows DD.MM.YYYY with a calendar and binds a `YYYY-MM-DD` string ('' when empty), the shape
 * the native date inputs it replaces gave the forms and the API calls.
 */
@Component({
	selector    : 'app-iso-date-field',
	standalone  : true,
	imports     : [ReactiveFormsModule, MatDatepickerModule, MatFormFieldModule, MatInputModule],
	templateUrl : './iso-date-field.component.html',
	styles      : [':host { display: block; } mat-form-field { width: 100%; }'],
	providers   : [
		{ provide : NG_VALUE_ACCESSOR, useExisting : forwardRef(() => IsoDateFieldComponent), multi : true },
		{ provide : MAT_DATE_LOCALE, useFactory : getMatDateLocale },
		{ provide : DateAdapter, useClass : MomentDateAdapter, deps : [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
		{ provide : MAT_DATE_FORMATS, useValue : DD_MM_YYYY_DATE_FORMATS },
	],
})
export class IsoDateFieldComponent implements ControlValueAccessor, OnDestroy {
	@Input({ required : true }) label!: string;
	@Input() subscript_sizing: SubscriptSizing = 'dynamic';

	@Input()
	set required(value: boolean | '')
	{
		this.is_required = value === '' || !!value;
		this.control.setValidators(this.is_required ? Validators.required : null);
		this.control.updateValueAndValidity({ emitEvent : false });
	}

	is_required = false;
	readonly control = new FormControl<Moment | null>(null);

	private onChange: ChangeHandler = NOOP;
	private onTouched: TouchHandler = NOOP;
	private changes: Subscription;

	constructor()
	{
		this.changes = this.control.valueChanges.subscribe(value => {
			this.onChange(value?.isValid() ? value.format(ISO_DATE) : '');
		});
	}

	ngOnDestroy(): void
	{
		this.changes.unsubscribe();
	}

	writeValue(value: string | null | undefined): void
	{
		const date = value ? moment(value, ISO_DATE) : null;
		this.control.setValue(date?.isValid() ? date : null, { emitEvent : false });
	}

	registerOnChange(fn: ChangeHandler): void
	{
		this.onChange = fn;
	}

	registerOnTouched(fn: TouchHandler): void
	{
		this.onTouched = fn;
	}

	setDisabledState(is_disabled: boolean): void
	{
		if (is_disabled) {
			this.control.disable({ emitEvent : false });

			return;
		}

		this.control.enable({ emitEvent : false });
	}

	markTouched(): void
	{
		this.onTouched();
	}
}
